import React from "react";

import cookie from "js-cookie";

import { loadScript } from "~/lib/hooks/useScript";
import recaptcha from "~/lib/recaptcha";
import { dispatchAnalyticsTrack, getCookiesOfInterest, getReferringEntities } from "~/lib/segment";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";

import { AuthBase } from "../AuthBase";
import { Button, EmptyHeader, Input, MobileHeader } from "../components/";
import { WhitelabelPane } from "../panes";
import type { AuthProps } from "../types";

import authStyles from "../css/Auth.module.css";
import styles from "../css/CaptivePortal.module.css";

interface CaptivePortalState {
	emailInput: string;
	emailSent: boolean;
	loading: boolean;
	errorMessage: string | undefined;
	showCalendly?: boolean;
}

export type CaptivePortalResult =
	| {
			success: true;
			showCalendly?: boolean;
			firstName?: string;
			lastName?: string;
	  }
	| {
			success: false;
			message: string | undefined;
	  };

export default class CaptivePortal extends AuthBase<AuthProps, CaptivePortalState> {
	state: CaptivePortalState = {
		emailInput: "",
		emailSent: false,
		loading: false,
		errorMessage: undefined,
	};

	documentTitle = "In-App Signup";

	getPaneComponent() {
		return WhitelabelPane;
	}

	componentDidMount(): void {
		super.componentDidMount();
		loadScript("https://assets.calendly.com/assets/external/widget.js", { type: "text/javascript" });
	}
	sendEmail = async () => {
		const { emailInput, loading } = this.state;
		if (!emailInput) {
			this.setState({ loading: false, errorMessage: "Enter your work email" });
			return;
		}

		if (loading) return;

		this.setState({ loading: true });

		const token = await recaptcha("captive_portal");
		const analyticsData = {
			feature: "Authentication",
			email: emailInput,
			...getReferringEntities(),
			...getCookiesOfInterest(),
		};

		const data = new FormData();
		data.append("email", emailInput);
		data.append("referrer", JSON.stringify(getReferringEntities()));
		data.append("captcha_token", token);

		const response = await fetch("/auth/captive/email/", {
			method: "POST",
			body: data,
			headers: {
				"x-client-session-id": window.CLIENT_SESSION_ID,
				"x-csrftoken": window.CSRF_TOKEN,
				"x-requested-with": "XMLHttpRequest",
			},
		}).catch(({ message }) => {
			dispatchAnalyticsTrack("Captive Portal Failed", analyticsData);
			this.setState({ loading: false, errorMessage: message });
		});

		if (!response) return this.setState({ loading: false, errorMessage: "Something went wrong" });
		let result: CaptivePortalResult;
		try {
			result = await response.json();
		} catch (e) {
			return this.setState({ loading: false, errorMessage: "Something went wrong" });
		}

		if (result.success) {
			dispatchAnalyticsTrack("Captive Portal Completed", analyticsData);
			if (result.showCalendly) {
				this.setState({ loading: false, emailSent: false, errorMessage: undefined, showCalendly: true });
				// @ts-ignore
				Calendly.initInlineWidget({
					url: "https://calendly.com/shane-bodie-fellow/fellow-ai-trial-onboarding?hide_gdpr_banner=1",
					parentElement: document.getElementById("calendly-fellow"),
					prefill: {
						firstName: result.firstName,
						lastName: result.lastName,
						email: this.state.emailInput,
					},
					utm: {
						// Calendly suggests putting custom params in the utm section to access in the webhook
						utmSource: JSON.stringify({ fbc: cookie.get("_fbc"), fbp: cookie.get("_fbp") }),
					},
				});
			} else {
				this.setState({ loading: false, emailSent: true, errorMessage: undefined });
			}
		} else {
			dispatchAnalyticsTrack("Captive Portal Failed", analyticsData);
			this.setState({ loading: false, errorMessage: result.message });
		}
	};

	onInputValueChange = (e: React.SyntheticEvent<HTMLInputElement, Event>) => {
		this.setState({ emailInput: e.currentTarget.value });
	};

	onInputKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			e.preventDefault();
			this.sendEmail();
		}
	};

	getMobileHeaderComponent = () => {
		return this.state.showCalendly ? EmptyHeader : MobileHeader;
	};

	clearInputValue = () => this.setState({ emailInput: "", errorMessage: undefined });

	renderEmailSent() {
		return (
			<div>
				<h5 className={authStyles.emailSent}>
					To set up Fellow, you will need to be on a computer. Setup instructions have been emailed to you. After Fellow
					is set up, you may access Fellow from the mobile app.
				</h5>
			</div>
		);
	}

	renderStep() {
		const { emailInput, loading, errorMessage, emailSent, showCalendly } = this.state;

		if (emailSent) return this.renderEmailSent();

		return (
			<>
				<div
					style={{ height: "100%", minWidth: "100%", display: showCalendly ? "block" : "none" }}
					id="calendly-fellow"
				></div>
				{!showCalendly && (
					<div className={styles.mainContainer}>
						<div className={styles.contentContainer}>
							<h2>Get started for FREE</h2>
							<h5>With the #1 AI meeting management solution</h5>

							<div className={styles.input}>
								<Input
									value={emailInput}
									error={!!errorMessage}
									placeholder="Enter your work email"
									onChange={this.onInputValueChange}
									onKeyDown={this.onInputKeydown}
									onClear={this.clearInputValue}
								/>
							</div>
						</div>

						<div className={styles.buttonContainer}>
							<Button primary onClick={this.sendEmail} disabled={loading} className={styles.authFindButton}>
								{loading ? (
									<LoadingSpinner className={authStyles.loadingSpinner} size="lg" inline dimmer={false} />
								) : (
									"Continue"
								)}
							</Button>
						</div>
					</div>
				)}
			</>
		);
	}
}
